import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
// FIX: Import NavLink to use in context type and state.
import type { PageContent, NavLink, Redirect, SocialLinks } from '../types';
import { produce } from "immer";
import { doc, getDoc, setDoc, collection, getDocs, terminate } from 'firebase/firestore';
import { db } from '../src/firebase';
import { defaultPageContent } from './DefaultPageContent'; // ✅ import default content
import { INDUSTRY_NAV_CHILDREN, industryPagesContentMap } from './industryPagesData';
import * as authService from '../services/authService';

const ensureIndustriesInHeaderNav = (navData: { links: NavLink[] }): { nav: { links: NavLink[] }; updated: boolean } => {
  const links = [...(navData?.links || [])];
  let updated = false;

  const industriesIdx = links.findIndex(
    (l) => l.id === 'industries' || l.name?.toLowerCase() === 'industries'
  );

  if (industriesIdx === -1) {
    // Insert Industries right after Home (or at index 1)
    const homeIdx = links.findIndex((l) => l.id === 'home' || l.path === '/');
    const insertIdx = homeIdx !== -1 ? homeIdx + 1 : 0;
    links.splice(insertIdx, 0, {
      id: 'industries',
      name: 'Industries',
      path: '/industries/',
      target: '_self',
      children: INDUSTRY_NAV_CHILDREN,
    });
    updated = true;
  } else {
    const existingItem = { ...links[industriesIdx] };
    const existingChildren = existingItem.children || [];
    const existingPaths = new Set(existingChildren.map((c) => c.path.replace(/\/$/, '')));
    const mergedChildren = [...existingChildren];

    INDUSTRY_NAV_CHILDREN.forEach((navChild) => {
      const normalized = navChild.path.replace(/\/$/, '');
      if (!existingPaths.has(normalized)) {
        mergedChildren.push(navChild);
        existingPaths.add(normalized);
        updated = true;
      }
    });

    if (!existingItem.path || existingItem.path === '#') {
      existingItem.path = '/industries/';
      updated = true;
    }

    existingItem.children = mergedChildren;
    links[industriesIdx] = existingItem;
  }

  const hasSocialAudit = links.some(
    (l) =>
      l.path === '/social-media-audit/' ||
      l.path === '/instagram-audit/' ||
      l.id === 'social-media-audit'
  );
  if (!hasSocialAudit) {
    const brandAuditIdx = links.findIndex(
      (l) => l.path === '/brand-audit/' || l.id === 'brand-audit'
    );
    const socialLink: NavLink = {
      id: 'social-media-audit',
      name: 'Social Media Audit',
      path: '/social-media-audit/',
      target: '_self',
    };
    if (brandAuditIdx !== -1) {
      links.splice(brandAuditIdx + 1, 0, socialLink);
    } else {
      links.push(socialLink);
    }
    updated = true;
  }

  return { nav: { links }, updated };
};

// Helper for deep merging defaults with stored data
const mergeDeep = (target: any, source: any) => {
  const output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target))
          Object.assign(output, { [key]: source[key] });
        else
          output[key] = mergeDeep(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

const isObject = (item: any) => {
  return (item && typeof item === 'object' && !Array.isArray(item));
};

// Default content structure: empty to prevent placeholder flash
// FIX: Export defaultContent to make it available for import in other modules.
export const defaultContent = {
  home: { h1: '', body: '', seo: { title: '', description: '', canonical: '/', slug: '/', customHead: '', noindex: false } },
  about: { h1: '', body: '', seo: { title: '', description: '', canonical: '/about/', slug: '/about/', customHead: '', noindex: false } },
  help: { h1: '', body: '', faqs: [], seo: { title: '', description: '', canonical: '/help/', slug: '/help/', customHead: '', noindex: false } },
  terms: { h1: '', body: '', seo: { title: '', description: '', canonical: '/terms/', slug: '/terms/', customHead: '', noindex: false } },
  contact: { h1: '', body: '', seo: { title: '', description: '', canonical: '/contact/', slug: '/contact/', customHead: '', noindex: false } },
  disclaimer: { h1: '', body: '', seo: { title: '', description: '', canonical: '/disclaimer/', slug: '/disclaimer/', customHead: '', noindex: false } },
  pricing: { h1: '', body: '', seo: { title: '', description: '', canonical: '/pricing/', slug: '/pricing/', customHead: '', noindex: false } },
  dashboard: { h1: '', welcomeMessage: '', seo: { title: '', description: '', canonical: '/dashboard/', slug: '/dashboard/', customHead: '', noindex: false } },
  blogIndex: { h1: 'Our Blog', body: 'Insights, strategies, and tips on building world-class brands.', seo: { title: 'Blog - BrandGenius', description: 'Read our latest articles on branding, naming, and business strategy.', canonical: '/blog/', slug: '/blog/', customHead: '', noindex: false } }
};

type AllContent = Record<string, PageContent>;


interface ContentContextType {
  content: AllContent;
  isLoaded: boolean;
  isAdminMode: boolean;
  toggleAdminMode: () => void;
  updatePageContent: (pageId: string, newContent: PageContent) => Promise<boolean>;
  // FIX: Add headerNav and footerNav properties to make them available to consumers of the context.
  headerNav?: { links: NavLink[] };
  footerNav?: { links: NavLink[] };
  socialLinks?: SocialLinks;
  redirects: Redirect[];
  addRedirect: (source: string, destination: string) => Promise<void>;
  deleteRedirect: (id: string) => Promise<void>;
  addPage: (pageId: string, pageData: PageContent) => Promise<void>;
  deletePage: (pageId: string) => Promise<void>;
  updateNavigation: (navId: 'headerNav' | 'footerNav', newNav: { links: NavLink[] }) => Promise<void>;
  updateSocialLinks: (links: SocialLinks) => Promise<void>;
}

export const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const ContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<AllContent>({ ...defaultContent, ...defaultPageContent });
  // FIX: Add state for header and footer navigation data.
  const [headerNav, setHeaderNav] = useState<{ links: NavLink[] } | undefined>();
  const [footerNav, setFooterNav] = useState<{ links: NavLink[] } | undefined>();
  const [socialLinks, setSocialLinks] = useState<SocialLinks | undefined>();
  const [redirects, setRedirects] = useState<Redirect[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);

  // Detect if the app is being run by react-snap for pre-rendering.
  const isPrerendering = navigator.userAgent.includes('ReactSnap');

  useEffect(() => {
    const fetchAndSeedContent = async () => {
      try {
        // Initialize combined content with static defaults
        const combinedContent: AllContent = { ...defaultContent, ...defaultPageContent };
        
        const idsInDb = new Set<string>();
        const pagesToUpgradeInDb: string[] = [];

        if (isPrerendering) {
          // In prerender mode, we fetch from the local JSON generated by prerender.js
          try {
            const res = await fetch('/prerender-data.json');
            if (res.ok) {
              const allData = await res.json();
              if (typeof window !== 'undefined') {
                (window as any).__PRERENDER_DATA__ = allData;
              }
              Object.keys(allData).forEach(id => {
                const data = allData[id];
                idsInDb.add(id);
                if (id === 'headerNav') {
                    const { nav } = ensureIndustriesInHeaderNav(data as { links: NavLink[] });
                    setHeaderNav(nav);
                } else if (id === 'footerNav') {
                    setFooterNav(data as { links: NavLink[] });
                } else if (id === 'socialLinks') {
                    setSocialLinks(data as SocialLinks);
                } else if (id === 'robots_txt' || id === 'llms_txt' || id === 'ads_txt' || id.startsWith('blogCategory_')) {
                    // Ignore text files and blog category contents
                } else {
                    if (id in industryPagesContentMap && (!data?.body || data.body.length < 3200)) {
                        combinedContent[id] = industryPagesContentMap[id];
                    } else if (combinedContent[id]) {
                        combinedContent[id] = mergeDeep(combinedContent[id], data) as PageContent;
                    } else {
                        combinedContent[id] = data as PageContent;
                    }
                }
              });
            }
          } catch (e) {
            console.error('Failed to load local prerender-data.json', e);
          }
        } else {
          // Fetch ALL documents from appContent collection to include dynamic pages
          const querySnapshot = await getDocs(collection(db, 'appContent'));
          
          for (const docSnap of querySnapshot.docs) {
              const id = docSnap.id;
              const data = docSnap.data();
              idsInDb.add(id);

              if (id === 'headerNav') {
                  const { nav, updated } = ensureIndustriesInHeaderNav(data as { links: NavLink[] });
                  setHeaderNav(nav);
                  if (updated) {
                    setDoc(doc(db, 'appContent', 'headerNav'), nav).catch(console.error);
                  }
              } else if (id === 'footerNav') {
                  setFooterNav(data as { links: NavLink[] });
              } else if (id === 'socialLinks') {
                  setSocialLinks(data as SocialLinks);
              } else if (id === 'robots_txt' || id === 'llms_txt' || id === 'ads_txt' || id.startsWith('blogCategory_')) {
                  // Ignore text files and blog category contents in the global content map
                  // to prevent ManagePages from crashing.
              } else {
                  if (id in industryPagesContentMap && (!data?.body || data.body.length < 3200)) {
                      combinedContent[id] = industryPagesContentMap[id];
                      pagesToUpgradeInDb.push(id);
                  } else if (combinedContent[id]) {
                      combinedContent[id] = mergeDeep(combinedContent[id], data) as PageContent;
                  } else {
                      combinedContent[id] = data as PageContent;
                  }
              }
          }
        }

        // Seed missing DEFAULT pages in Firestore, and upgrade thin legacy industry pages
        if (!isPrerendering) {
          for (const [pageId, defaultData] of Object.entries(combinedContent)) {
               const isDefaultPage = pageId in defaultContent || pageId in defaultPageContent;
               
               if ((isDefaultPage && !idsInDb.has(pageId)) || pagesToUpgradeInDb.includes(pageId)) {
                  const docRef = doc(db, 'appContent', pageId);
                  setDoc(docRef, defaultData).catch(console.error);
               }
          }
        }

        setContent(combinedContent);

        // --- Navigation & Social Links Seeding ---
        const defaultHeaderNav = {
          links: [
            { id: 'home', name: 'Home', path: '/' },
            { id: 'industries', name: 'Industries', path: '/industries/', children: INDUSTRY_NAV_CHILDREN },
            { id: 'brand-audit', name: 'Brand Audit', path: '/brand-audit/' },
            { id: 'ai-prompts', name: 'AI Prompts', path: '/ai-prompt-generator/' },
            { id: 'blog', name: 'Blog', path: '/blog/' },
            { id: 'pricing', name: 'Pricing', path: '/pricing/' },
            { id: 'about', name: 'About', path: '/about/' },
            { id: 'help', name: 'Help', path: '/help/' },
            { id: 'contact', name: 'Contact', path: '/contact/' },
          ],
        };

        const defaultFooterNav = {
          links: [
            { id: 'blog', name: 'Blog', path: '/blog/' },
            { id: 'terms', name: 'Terms of Service', path: '/terms/' },
            { id: 'disclaimer', name: 'Disclaimer', path: '/disclaimer/' },
            { id: 'about', name: 'About', path: '/about/' },
            { id: 'contact', name: 'Contact', path: '/contact/' },
          ],
        };
        
        const defaultSocialLinks = {
          facebook: '',
          instagram: '',
          pinterest: '',
          threads: '',
          youtube: '',
          linkedin: '',
        };

        if (!isPrerendering && !idsInDb.has('headerNav')) {
          console.log("Seeding headerNav content in Firestore.");
          await setDoc(doc(db, 'appContent', 'headerNav'), defaultHeaderNav);
          setHeaderNav(defaultHeaderNav);
        } else if (isPrerendering && !idsInDb.has('headerNav')) {
          setHeaderNav(defaultHeaderNav);
        }

        if (!isPrerendering && !idsInDb.has('footerNav')) {
          console.log("Seeding footerNav content in Firestore.");
          await setDoc(doc(db, 'appContent', 'footerNav'), defaultFooterNav);
          setFooterNav(defaultFooterNav);
        } else if (isPrerendering && !idsInDb.has('footerNav')) {
          setFooterNav(defaultFooterNav);
        }

        if (!isPrerendering && !idsInDb.has('socialLinks')) {
           console.log("Seeding socialLinks content in Firestore.");
           await setDoc(doc(db, 'appContent', 'socialLinks'), defaultSocialLinks);
           setSocialLinks(defaultSocialLinks);
        } else if (isPrerendering && !idsInDb.has('socialLinks')) {
           setSocialLinks(defaultSocialLinks);
        }
        
        // Fetch redirects
        if (!isPrerendering) {
          const fetchedRedirects = await authService.getRedirects();
          setRedirects(fetchedRedirects);
        }

      } catch (error: any) {
        if (error.code === 'unavailable') {
          console.warn("Application is offline. Using cached or default content. Data will sync when reconnected.");
        } else {
          console.error("Failed to load or seed content from Firestore", error);
        }
        setContent({ ...defaultContent, ...defaultPageContent });
      } finally {
        setIsLoaded(true);
        if (isPrerendering) {
          terminate(db).catch(console.error);
        }
      }
    };

    // We now always fetch from Firebase, even during pre-rendering,
    // so that dynamically added pages (like streetwear-brand-name-generator)
    // are present in the state and can be rendered and have their SEO tags injected.
    fetchAndSeedContent();
  }, [isPrerendering]);

  const toggleAdminMode = () => {
    setIsAdminMode(prev => !prev);
  };
  
  const addRedirect = useCallback(async (source: string, destination: string) => {
    const newRedirect = await authService.addRedirect(source, destination);
    setRedirects(prev => [newRedirect, ...prev]);
  }, []);

  const updatePageContent = useCallback(async (pageId: string, newContent: PageContent): Promise<boolean> => {
      const oldContent = content[pageId];
      const oldSlug = oldContent?.seo?.slug;
      const newSlug = newContent?.seo?.slug;
      let redirectCreated = false;

      if (oldSlug && newSlug && oldSlug !== newSlug) {
        const sourceExists = redirects.some(r => r.source === oldSlug);
        if (!sourceExists) {
            try {
                await addRedirect(oldSlug, newSlug);
                redirectCreated = true;
            } catch (redirectError) {
                console.error(`Failed to create redirect for '${pageId}'`, redirectError);
            }
        }
      }

      setContent(produce(draft => {
        draft[pageId] = newContent;
      }));

      try {
        const docRef = doc(db, 'appContent', pageId);
        await setDoc(docRef, newContent);
      } catch (error) {
         console.error(`Failed to save content for '${pageId}' to Firestore`, error);
      }

      return redirectCreated;
  }, [content, redirects, addRedirect]);

  const deleteRedirect = useCallback(async (id: string) => {
    await authService.deleteRedirect(id);
    setRedirects(prev => prev.filter(r => r.id !== id));
  }, []);

  const addPage = useCallback(async (pageId: string, pageData: PageContent) => {
    await authService.addPage(pageId, pageData);
    setContent(produce(draft => {
      draft[pageId] = pageData;
    }));
  }, []);

  const deletePage = useCallback(async (pageId: string) => {
      await authService.deletePage(pageId);
      setContent(produce(draft => {
        delete draft[pageId];
      }));
  }, []);

  const updateNavigation = useCallback(async (navId: 'headerNav' | 'footerNav', newNav: { links: NavLink[] }) => {
      await authService.updateNavigation(navId, newNav);
      if (navId === 'headerNav') {
          setHeaderNav(newNav);
      } else {
          setFooterNav(newNav);
      }
  }, []);

  const updateSocialLinks = useCallback(async (links: SocialLinks) => {
    await authService.updateSocialLinks(links);
    setSocialLinks(links);
  }, []);


  return (
    <ContentContext.Provider value={{ 
        content, isLoaded, isAdminMode, toggleAdminMode, updatePageContent, headerNav, footerNav, socialLinks, redirects, addRedirect, deleteRedirect,
        addPage, deletePage, updateNavigation, updateSocialLinks
      }}>
      {children}
    </ContentContext.Provider>
  );
};